/**
 * <FloorRoomPicker> · ADR-FO-1 D7
 * ────────────────────────────────────────────────────────────────
 * Visual room picker: floor tabs + large room cards + search + filter.
 * Never use dropdowns for room selection.
 */
import React, { useMemo, useState } from "react";
import { Filter } from "lucide-react";
import { LargeSearchBox } from "./index";
import { RoomOperationalCard } from "./RoomOperationalCard";

export function FloorRoomPicker({
  rooms = [],          // [{id, number, floor, type_label, snapshot_status, ...}]
  selectedRoomId,
  onSelect,
  loading = false,
  emptyLabel = "No rooms available",
  allowedStatuses,     // optional array — filters to only these snapshot_status keys
  testid = "floor-room-picker",
}) {
  const [floor, setFloor] = useState("all");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const floors = useMemo(() => {
    const set = new Set(rooms.map(r => r.floor ?? "?").filter(Boolean));
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [rooms]);

  const statuses = useMemo(() => {
    const set = new Set(rooms.map(r => r.snapshot_status).filter(Boolean));
    return Array.from(set).sort();
  }, [rooms]);

  const filtered = useMemo(() => {
    let out = rooms;
    if (allowedStatuses) out = out.filter(r => allowedStatuses.includes(r.snapshot_status));
    if (floor !== "all") out = out.filter(r => (r.floor ?? "?") === floor);
    if (statusFilter !== "all") out = out.filter(r => r.snapshot_status === statusFilter);
    if (q) {
      const s = q.toLowerCase();
      out = out.filter(r =>
        String(r.number || "").toLowerCase().includes(s) ||
        String(r.type_label || "").toLowerCase().includes(s) ||
        String(r.current_guest || "").toLowerCase().includes(s));
    }
    return out;
  }, [rooms, floor, q, statusFilter, allowedStatuses]);

  return (
    <div data-testid={testid} className="space-y-3">
      {/* Search */}
      <LargeSearchBox
        value={q}
        onChange={setQ}
        placeholder="Search by room number, type, or guest"
        testid={`${testid}-search`}/>

      {/* Floor tabs */}
      <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-floors`}>
        <FilterTab
          label={`All (${rooms.length})`}
          active={floor === "all"}
          onClick={() => setFloor("all")}
          testid={`${testid}-floor-all`}/>
        {floors.map(f => {
          const n = rooms.filter(r => (r.floor ?? "?") === f).length;
          return (
            <FilterTab key={f}
              label={`Floor ${f} (${n})`}
              active={floor === f}
              onClick={() => setFloor(f)}
              testid={`${testid}-floor-${f}`}/>
          );
        })}
      </div>

      {/* Status filter chips */}
      {statuses.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testid}-status-filter`}>
          <Filter className="w-3.5 h-3.5 text-slate-400"/>
          <FilterTab label="Any status" active={statusFilter === "all"}
                        onClick={() => setStatusFilter("all")}
                        testid={`${testid}-status-all`} small/>
          {statuses.map(s => (
            <FilterTab key={s} label={s} active={statusFilter === s}
                          onClick={() => setStatusFilter(s)}
                          testid={`${testid}-status-${s}`} small/>
          ))}
        </div>
      )}

      {/* Rooms grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
                data-testid={`${testid}-loading`}>
          {[0,1,2,3,4,5].map(i => (
            <RoomOperationalCard key={i} room={null} loading testid={`${testid}-load-${i}`}/>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-slate-500 border-2 border-dashed border-slate-200 rounded-xl"
                data-testid={`${testid}-empty`}>
          {emptyLabel}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
                data-testid={`${testid}-grid`}>
          {filtered.map(r => {
            const blocked = r.assignable === false;
            return (
              <div key={r.id || r.number} className="relative">
                <RoomOperationalCard
                  room={r}
                  size="sm"
                  onClick={blocked ? undefined : () => onSelect?.(r)}
                  disabled={blocked}
                  testid={`${testid}-room-${r.number}`}
                />
                {blocked && (
                  <>
                    <div className="absolute inset-0 pointer-events-none rounded-xl bg-slate-900/5" />
                    <span
                      className="absolute top-2 right-2 z-10 pointer-events-none text-[10px] font-semibold text-red-700 bg-white/95 border border-red-200 rounded px-1.5 py-0.5 leading-tight shadow-sm"
                      title={r.block_reason || "Not assignable"}
                      data-testid={`${testid}-block-${r.number}`}
                    >Blocked</span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterTab({ label, active, onClick, testid, small }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={[
        small ? "px-2 py-1 text-[11px]" : "px-3 py-2 text-[13px]",
        "rounded-lg border font-medium transition-colors",
        "min-h-[36px]",
        active
          ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint"
          : "bg-white text-slate-700 border-slate-300 hover:border-slate-500",
      ].join(" ")}
    >{label}</button>
  );
}
